'use client'

import Box from '@mui/material/Box'
import type { PaperProps } from '@mui/material/Paper'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { ReactNode } from 'react'

interface DataCardProps extends Omit<PaperProps, 'className' | 'title'> {
  children: ReactNode
  /** Optional card header title, rendered as subtitle1. */
  title?: ReactNode
  subtitle?: ReactNode
  /** Right-aligned header actions (buttons, chips, menus). */
  actions?: ReactNode
  noPadding?: boolean
  /** Tighter 16px padding for dense data displays. */
  dense?: boolean
  /** Constrains the card body and makes it scrollable. */
  maxHeight?: number | string
}

export default function DataCard({
  children,
  title,
  subtitle,
  actions,
  noPadding = false,
  dense = false,
  maxHeight,
  sx,
  ...props
}: DataCardProps) {
  const padding = noPadding ? 0 : dense ? 2 : 3
  const hasHeader = title || subtitle || actions

  return (
    <Paper
      className="admin-card"
      sx={{ p: padding, overflow: 'hidden', ...sx }}
      {...props}
    >
      {hasHeader && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: subtitle ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Stack spacing={0.5}>
            {title && <Typography variant="subtitle1">{title}</Typography>}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Stack>
          {actions && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              {actions}
            </Stack>
          )}
        </Stack>
      )}
      {maxHeight ? (
        <Box sx={{ maxHeight, overflow: 'auto' }}>{children}</Box>
      ) : (
        children
      )}
    </Paper>
  )
}
